import { defaultTo, difference, get, intersection, pick, uniq } from 'lodash';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import isEqual from 'fast-deep-equal';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseBatchChartRenderQueueProps {
  parallelRender: number;
}
interface ChartMap {
  [key: string]: number;
}

type Queue = string[];

const cleanQueue = (queue: Queue, chartIds: string[]) => {
  const newQueue = intersection(queue, chartIds);

  if (newQueue.length === queue.length) return queue;

  return newQueue;
};

const cleanObject = (object: { [key: string]: number }, chartIds: string[]) => {
  const newObject = pick(object, chartIds);

  if (Object.keys(newObject).length === Object.keys(object).length) {
    return object;
  }

  return newObject;
};

const useBatchChartRenderQueue = ({
  parallelRender
}: UseBatchChartRenderQueueProps) => {
  const chartsVisibilityRef = useRef<ChartMap>({});

  const [dataRevisions, setDataRevisions] = useState<ChartMap>({});
  const [dataPanelsRows, setDataPanelsRows] = useState({});
  const [renderingQueue, setRenderingQueue] = useState<Queue>([]);
  const [renderQueue, setRenderQueue] = useState<Queue>([]);

  const removeRenderingQueue = useCallback((chartId: string) => {
    setRenderingQueue(queue => {
      const newQueue = queue.filter(queueId => queueId !== chartId);

      if (newQueue.length === queue.length) return queue;

      return newQueue;
    });
  }, []);

  const addRenderQueue = useCallback((chartId: string) => {
    setRenderQueue(queue => {
      if (queue.includes(chartId)) return queue;

      return [...queue, chartId];
    });
  }, []);

  const onChartVisibilityChange = useCallback((id, inView, entry) => {
    const value = get(entry, 'intersectionRatio', 0);

    chartsVisibilityRef.current[id] = value;
  }, []);

  const onUpdateDataPanelRows = useCallback((id, name, rows) => {
    setDataPanelsRows(previousDataPanelsRows => ({
      ...previousDataPanelsRows,
      [name]: rows
    }));
  }, []);

  const getDataRevision = useCallback(id => defaultTo(dataRevisions[id], 0), [
    dataRevisions
  ]);

  useEffect(() => {
    if (!renderQueue.length) return;

    const renderAmount = parallelRender - renderingQueue.length;

    if (renderAmount <= 0) return;

    const renderQueueOrdered = [...renderQueue].sort((idA, idB) => {
      const visibilityA = get(chartsVisibilityRef.current, idA, 0);
      const visibilityB = get(chartsVisibilityRef.current, idB, 0);

      return visibilityB - visibilityA;
    });

    const currentRenders = renderQueueOrdered.slice(0, parallelRender);

    batchedUpdates(() => {
      setDataRevisions(currentDataRevisions => {
        const newDataRevisions = currentRenders.reduce<ChartMap>(
          (result, id) => {
            // eslint-disable-next-line no-param-reassign
            result[id] = defaultTo(currentDataRevisions[id], 0) + 1;

            return result;
          },
          {}
        );

        return {
          ...currentDataRevisions,
          ...newDataRevisions
        };
      });
      setRenderingQueue(queue => uniq([...queue, ...currentRenders]));
      setRenderQueue(queue => {
        const pendingRenderQueue = difference(queue, currentRenders);

        if (isEqual(queue, pendingRenderQueue)) return queue;

        return pendingRenderQueue;
      });
    });
  }, [parallelRender, renderingQueue, renderQueue]);

  const cleanRenderQueue = useCallback(chartIds => {
    chartsVisibilityRef.current = cleanObject(
      chartsVisibilityRef.current,
      chartIds
    );

    batchedUpdates(() => {
      setDataRevisions(o => cleanObject(o, chartIds));
      setRenderingQueue(q => cleanQueue(q, chartIds));
      setRenderQueue(q => cleanQueue(q, chartIds));
    });
  }, []);

  return {
    renderQueue,
    dataPanelsRows,
    addRenderQueue,
    removeRenderingQueue,
    onUpdateDataPanelRows,
    getDataRevision,
    onChartVisibilityChange,
    cleanRenderQueue
  };
};

export default useBatchChartRenderQueue;
